/**
 * @file More view — menu hub for Timer, Camera, Invoices, Settings, Sync, Logout
 */

import { getCurrentUser, doLogout } from '@auth/index.js';
import { getStorageUsage, sbPullAll, load } from '@state/store.js';
import { isOnline } from '@state/sync.js';
import { config } from '@core/config.js';
import { navigate } from '@core/router.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';

function render() {
  const user = getCurrentUser();
  const storage = getStorageUsage();
  const online = isOnline();
  const lastSync = load('_sb_last_sync', 0);
  const lastSyncStr = lastSync ? new Date(lastSync).toLocaleString() : 'Never';
  const invoiceCount = load('saved_invoices', []).length;

  return `
    <div class="view-header">
      <h1>More</h1>
    </div>

    <div class="card mb-md">
      <div style="font-weight:600;font-size:1rem;">${esc(user?.user_metadata?.full_name || user?.email || 'User')}</div>
      <div class="text-muted text-sm">${esc(user?.email || '')}</div>
    </div>

    <!-- Menu Items -->
    <div class="detail-section">
      <div class="detail-section-header">Tools</div>
      <div class="detail-section-content" style="padding:0;">
        <div class="settings-item more-menu-item" data-nav="timer">
          <span class="settings-item-label">\u23F1\uFE0F Timer</span>
          <span class="settings-item-value">\u203A</span>
        </div>
        <div class="settings-item more-menu-item" data-nav="camera">
          <span class="settings-item-label">\uD83D\uDCF7 Camera</span>
          <span class="settings-item-value">\u203A</span>
        </div>
        <div class="settings-item more-menu-item" id="invoices-menu-item">
          <span class="settings-item-label">\uD83D\uDCB0 Invoices</span>
          <span class="settings-item-value">${invoiceCount} saved \u203A</span>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">Sync</div>
      <div class="detail-section-content">
        <div class="settings-item">
          <span class="settings-item-label">Status</span>
          <span class="settings-item-value" style="color:${online ? 'var(--green)' : 'var(--orange)'};">
            ${online ? 'Online' : 'Offline'}
          </span>
        </div>
        <div class="settings-item">
          <span class="settings-item-label">Last Sync</span>
          <span class="settings-item-value">${lastSyncStr}</span>
        </div>
        <button class="btn btn-outline btn-full mt-sm" id="sync-btn">Sync Now</button>
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">Storage</div>
      <div class="detail-section-content">
        <div class="flex-between mb-sm">
          <span class="text-sm">${storage.totalMB} MB used</span>
          <span class="text-sm text-muted">${storage.usedPct}% of 5 MB</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" style="width:${Math.min(storage.usedPct, 100)}%;${storage.usedPct > 80 ? 'background:var(--orange);' : ''}"></div>
        </div>
      </div>
    </div>

    <div class="detail-section">
      <div class="detail-section-header">App</div>
      <div class="detail-section-content">
        <div class="settings-item">
          <span class="settings-item-label">Version</span>
          <span class="settings-item-value">${config.app.version}</span>
        </div>
        <div class="settings-item">
          <span class="settings-item-label">App</span>
          <span class="settings-item-value">${config.app.name}</span>
        </div>
        <a href="https://pbfcommandcenter.pages.dev" target="_blank" class="settings-item" style="text-decoration:none;">
          <span class="settings-item-label" style="color:var(--blue);">Open Web CRM</span>
          <span class="settings-item-value">\u2197</span>
        </a>
      </div>
    </div>

    <button class="btn btn-danger btn-full mt-lg" id="logout-btn">Sign Out</button>
  `;
}

function mount() {
  // Nav menu items
  document.querySelectorAll('.more-menu-item[data-nav]').forEach(item => {
    item.addEventListener('click', () => {
      navigate(item.dataset.nav);
    });
  });

  // Invoices
  document.getElementById('invoices-menu-item')?.addEventListener('click', () => {
    showInvoiceList();
  });

  // Sync button
  document.getElementById('sync-btn')?.addEventListener('click', async () => {
    toast('Syncing...', 'info');
    await sbPullAll();
    toast('Sync complete', 'success');
    rerender();
  });

  // Logout button
  document.getElementById('logout-btn')?.addEventListener('click', () => {
    showModal('Sign Out', '<p>Are you sure you want to sign out?</p>', [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Sign Out', type: 'danger', onClick: async () => {
        closeModal();
        await doLogout();
        toast('Signed out', 'info');
      }},
    ]);
  });
}

function showInvoiceList() {
  const invoices = load('saved_invoices', []);
  if (invoices.length === 0) {
    toast('No invoices yet', 'info');
    return;
  }

  const invoiceHtml = invoices.slice(0, 20).map(inv => {
    const date = inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const status = inv.status || 'draft';
    const statusBadge = status === 'paid' ? 'badge-green' : status === 'sent' ? 'badge-blue' : 'badge-gray';
    return `
      <div class="settings-item">
        <div>
          <div style="font-weight:600;">${esc(inv.customerName || 'Unknown')}</div>
          <div class="text-muted text-sm">${date} - $${(inv.total || 0).toLocaleString()}</div>
        </div>
        <span class="badge ${statusBadge}">${status}</span>
      </div>
    `;
  }).join('');

  showModal('Invoices', `<div style="max-height:60vh;overflow-y:auto;">${invoiceHtml}</div>`, [
    { label: 'Close', onClick: closeModal },
  ]);
}

function rerender() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default { render, mount, unmount };
